import { Fragment } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { SearchableValuePicker } from "@/components/shared/searchable-value-picker";
import { getGetApiAgentsAgentIdQueryKey } from "@/lib/api/generated/agents/agents";
import {
  getGetApiConnectorPackagesQueryKey,
  useGetApiConnectorPackages,
  usePostApiConnectorPackagesConnectorPackageIdActivate,
} from "@/lib/api/generated/connector-packages/connector-packages";
import { getGetApiConnectorInstallsQueryKey } from "@/lib/api/generated/channels/channels";
import { queryKeys } from "@/lib/query/keys";
import { toastMutationError } from "@/lib/utils/formatting";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { useAgentPolicyForm } from "./use-agent-policy-form";
import {
  EXECUTION_CLASS_OPTIONS,
  MEMORY_EMBEDDING_DIMENSION_OPTIONS,
  MODEL_SLOT_DEFS,
  THINKING_DEFAULT_OPTIONS,
} from "./agent-policy-utils";
import { AgentTabSurface } from "./agent-tab-surface";

export function AgentPolicyTab() {
  const { agentId } = useParams<{ agentId: string }>();
  const { tenantId } = useTenantContext();
  const queryClient = useQueryClient();
  const connectorPackages = useGetApiConnectorPackages();
  const activateConnectorPackage = usePostApiConnectorPackagesConnectorPackageIdActivate();
  const {
    form,
    updateForm,
    updateModelSlot,
    isDirty,
    isPending,
    save,
    discard,
    viewMode,
    setViewMode,
    modelPickerOptions,
    embeddingModelPickerOptions,
    connectedProviders,
    policyViewModel,
    setConnectorRowEnabled,
    setConnectorDefaultTarget,
  } = useAgentPolicyForm();

  if (viewMode === "raw") {
    return (
      <AgentTabSurface
        title="Runtime Policy"
        description="Raw JSON is the escape hatch. Structured mode remains the primary editor."
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => setViewMode("structured")}>
              Back To Structured
            </Button>
            {isDirty ? (
              <Button variant="ghost" size="sm" onClick={discard}>
                Discard
              </Button>
            ) : null}
            <Button size="sm" onClick={save} disabled={isPending}>
              {isPending ? "Saving..." : "Save"}
            </Button>
          </>
        }
      >
        <JsonTextarea
          value={form.rawJson}
          onChange={(value) => updateForm("rawJson", value)}
          rows={24}
        />
      </AgentTabSurface>
    );
  }

  return (
    <AgentTabSurface
      title="Runtime Policy"
      description="Select the workflows, sources, and connector-backed tools this agent can use."
      actions={
        <>
          {isDirty ? (
            <Button variant="ghost" size="sm" onClick={discard}>
              Discard
            </Button>
          ) : null}
          <Button size="sm" onClick={save} disabled={isPending}>
            {isPending ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col">
        <PolicySection
          title="What This Agent Can Use"
          description="Capability-first access. Low-level connector keys and binding policy are derived from these selections."
        >
          <FieldGroup label="Workflows">
            <SearchableValuePicker
              values={form.enabledWorkflowKeys}
              onChange={(values) => updateForm("enabledWorkflowKeys", values)}
              options={policyViewModel.workflowPickerOptions}
              placeholder="Search tenant workflows"
            />
          </FieldGroup>

          <FieldGroup label="Allowed Tools">
            <SearchableValuePicker
              values={form.allowedToolNames}
              onChange={(values) => updateForm("allowedToolNames", values)}
              options={policyViewModel.allowedToolOptions}
              placeholder="Search tools or type a custom tool name"
            />
          </FieldGroup>

          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <p className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
                Connector Packages
              </p>
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                Activate published connector packages for this agent. The package remains reusable across other agents.
              </p>
            </div>
            <ConnectorPackageActivationRows
              agentId={agentId}
              packages={connectorPackages.data ?? []}
              loading={connectorPackages.isLoading}
              pending={activateConnectorPackage.isPending}
              onActivate={(connectorPackageId) => {
                if (!agentId) return;
                activateConnectorPackage.mutate(
                  {
                    connectorPackageId,
                    data: { agentId },
                  },
                  {
                    onSuccess: () => {
                      toast.success("Connector package activated");
                      void queryClient.invalidateQueries({ queryKey: getGetApiConnectorPackagesQueryKey() });
                      void queryClient.invalidateQueries({ queryKey: getGetApiAgentsAgentIdQueryKey(agentId) });
                      void queryClient.invalidateQueries({ queryKey: getGetApiConnectorInstallsQueryKey() });
                      void queryClient.invalidateQueries({ queryKey: queryKeys.providerDescriptors(tenantId) });
                      void queryClient.invalidateQueries({ queryKey: queryKeys.tenantCapabilityBindings(tenantId) });
                      void queryClient.invalidateQueries({ queryKey: queryKeys.tenantCapabilityTargets(tenantId) });
                    },
                    onError: (error) => toast.error(toastMutationError(error, "Failed to activate connector package")),
                  },
                );
              }}
            />

            <div className="space-y-1">
              <p className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
                Connectors
              </p>
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                Each enabled row becomes an agent-usable capability. GraphRAG sources are target-scoped and can set a default per binding.
              </p>
            </div>

            {policyViewModel.connectorSections.length === 0 ? (
              <EmptyHint>
                No active connector-backed capabilities found. Configure connector installs, bindings, and targets first.
              </EmptyHint>
            ) : (
              <div className="flex flex-col gap-4">
                {policyViewModel.connectorSections.map((section) => (
                  <ConnectorSection
                    key={section.id}
                    title={section.title}
                    description={section.description}
                    rows={section.rows}
                    onToggle={setConnectorRowEnabled}
                    onSetDefault={setConnectorDefaultTarget}
                  />
                ))}
              </div>
            )}
          </div>
        </PolicySection>

        <PolicySection
          title="How It Runs"
          description="Operational runtime settings, separate from the capabilities the agent is allowed to use."
        >
          <FieldGroup label="Reasoning depth">
            <Select
              value={form.thinkingDefault}
              onValueChange={(value) =>
                updateForm("thinkingDefault", value as typeof form.thinkingDefault)
              }
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="Select reasoning depth" />
              </SelectTrigger>
              <SelectContent>
                {THINKING_DEFAULT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldGroup>

          <BooleanRow
            checked={form.allowUserThinkingOverride}
            label="Allow User Reasoning Override"
            description="Let web and mobile users choose the reasoning level for their current session."
            onChange={(checked) => updateForm("allowUserThinkingOverride", checked)}
          />

          <FieldGroup label="Execution Class">
            <Select
              value={form.executionClass}
              onValueChange={(value) => updateForm("executionClass", value as typeof form.executionClass)}
            >
              <SelectTrigger className="w-full bg-[var(--bg-elevated)]">
                <SelectValue placeholder="Select execution class" />
              </SelectTrigger>
              <SelectContent>
                {EXECUTION_CLASS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldGroup>

          <div className="grid gap-4 md:grid-cols-2">
            <FieldGroup label="Memory Embedding Model">
              <SearchableValuePicker
                values={form.memoryEmbeddingModel ? [form.memoryEmbeddingModel] : []}
                onChange={(next) => updateForm("memoryEmbeddingModel", next[0] ?? "")}
                options={embeddingModelPickerOptions}
                multiple={false}
                placeholder="Search embedding models or type provider/model"
              />
            </FieldGroup>

            <FieldGroup label="Memory Embedding Dimensions">
              <Select
                value={form.memoryEmbeddingDimensions || "provider-default"}
                onValueChange={(value) =>
                  updateForm(
                    "memoryEmbeddingDimensions",
                    value === "provider-default" ? "" : value,
                  )
                }
              >
                <SelectTrigger className="w-full bg-[var(--bg-elevated)]">
                  <SelectValue placeholder="Provider default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="provider-default">Provider Default</SelectItem>
                  {MEMORY_EMBEDDING_DIMENSION_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldGroup>
          </div>

          <BooleanRow
            checked={form.activeMemoryEnabled}
            label="Active Memory Recall"
            description="Run the configured memory recall hook before conversational replies."
            onChange={(checked) => updateForm("activeMemoryEnabled", checked)}
          />

          <BooleanRow
            checked={form.sessionMemoryEnabled}
            label="Session Memory Archive"
            description="Archive the previous session into durable user memory when a session is reset."
            onChange={(checked) => updateForm("sessionMemoryEnabled", checked)}
          />

          <BooleanRow
            checked={form.memoryFlushEnabled}
            label="Compaction Memory Flush"
            description="Capture durable notes before Pi compacts long-running conversations."
            onChange={(checked) => updateForm("memoryFlushEnabled", checked)}
          />

          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
            <BooleanRow
              checked={form.memoryDreamingEnabled}
              label="Memory Dreaming"
              description="Run scheduled memory maintenance that promotes useful recall signals."
              onChange={(checked) => updateForm("memoryDreamingEnabled", checked)}
            />
            <FieldGroup label="Dreaming Cadence">
              <input
                value={form.memoryDreamingFrequency}
                onChange={(event) => updateForm("memoryDreamingFrequency", event.target.value)}
                placeholder="0 3 * * *"
                className="h-10 w-full rounded-md border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-3 text-sm"
                style={{ color: "var(--text-strong)" }}
              />
            </FieldGroup>
          </div>
        </PolicySection>

        <PolicySection
          title="Safety And Advanced Settings"
          description="Operator safeguards and lower-level policy overrides."
        >
          <FieldGroup label="Dangerous Tool Deny">
            <SearchableValuePicker
              values={form.dangerousToolDeny}
              onChange={(values) => updateForm("dangerousToolDeny", values)}
              options={policyViewModel.dangerousToolOptions}
              placeholder="Search connector-backed tool names"
            />
          </FieldGroup>

          <div className="grid gap-3 md:grid-cols-2">
            <BooleanRow
              checked={form.allowCrossSummary}
              label="Allow Cross-Conversation Summary Access"
              description="Permit the runtime to read summarized state from other conversations."
              onChange={(checked) => updateForm("allowCrossSummary", checked)}
            />
          </div>

          <div className="rounded-[var(--radius-md)] bg-[var(--bg-elevated)] px-4 py-3">
            <button
              type="button"
              onClick={() => updateForm("advancedOpen", !form.advancedOpen)}
              className="flex w-full items-center justify-between py-2 text-left"
            >
                <span>
                  <span className="block text-sm font-medium" style={{ color: "var(--text-strong)" }}>
                    Advanced Editors
                  </span>
                  <span className="block text-xs" style={{ color: "var(--text-muted)" }}>
                    Model overrides, memory JSON, and the raw policy editor.
                  </span>
                </span>
                <span
                  className="text-xs transition-transform"
                  style={{
                    color: "var(--text-muted)",
                    transform: form.advancedOpen ? "rotate(90deg)" : "rotate(0deg)",
                  }}
                >
                  &#9654;
                </span>
            </button>

            {form.advancedOpen ? (
              <div className="pt-4">
                <Separator className="mb-4" />
                <div className="mb-4 flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setViewMode("raw")}>
                    Edit Raw JSON
                  </Button>
                </div>

                <div className="flex flex-col gap-4">
                    <FieldGroup label="Memory Policy">
                      <JsonTextarea
                        value={form.memoryPolicyJson}
                        onChange={(value) => updateForm("memoryPolicyJson", value)}
                      />
                    </FieldGroup>
                    <FieldGroup label="Response Style">
                      <JsonTextarea
                        value={form.responseStyleJson}
                        onChange={(value) => updateForm("responseStyleJson", value)}
                      />
                    </FieldGroup>

                    <div className="grid gap-4 md:grid-cols-2">
                      <p className="md:col-span-2 text-xs" style={{ color: "var(--text-muted)" }}>
                        {connectedProviders.length > 0
                          ? `Connected model providers: ${connectedProviders.join(", ")}`
                          : "No active model-provider connections yet. Save and validate a provider on AI Providers before picking tenant-backed models here."}
                      </p>

                      {MODEL_SLOT_DEFS.map((definition) => (
                        <Fragment key={definition.key}>
                          <FieldGroup label={definition.label}>
                            <SearchableValuePicker
                              values={
                                form.modelSlots[definition.key].primary
                                  ? [form.modelSlots[definition.key].primary]
                                  : []
                              }
                              onChange={(next) =>
                                updateModelSlot(definition.key, { primary: next[0] ?? "" })
                              }
                              options={modelPickerOptions}
                              multiple={false}
                              placeholder="Search models or type provider/model"
                            />
                          </FieldGroup>
                          {definition.hasFallbacks ? (
                            <FieldGroup label={`${definition.label} Fallbacks`}>
                              <SearchableValuePicker
                                values={form.modelSlots[definition.key].fallbacks}
                                onChange={(fallbacks) =>
                                  updateModelSlot(definition.key, { fallbacks })
                                }
                                options={modelPickerOptions}
                                placeholder="Search fallback models or type provider/model"
                              />
                            </FieldGroup>
                          ) : null}
                        </Fragment>
                      ))}
                    </div>

                    <FieldGroup label="Model Catalog Overrides">
                      <JsonTextarea
                        value={form.modelEntriesJson}
                        onChange={(value) => updateForm("modelEntriesJson", value)}
                        rows={8}
                      />
                    </FieldGroup>
                    <FieldGroup label="Agent Model Params">
                      <JsonTextarea
                        value={form.modelParamsJson}
                        onChange={(value) => updateForm("modelParamsJson", value)}
                        rows={6}
                      />
                    </FieldGroup>
                </div>
              </div>
            ) : null}
          </div>
        </PolicySection>
      </div>
    </AgentTabSurface>
  );
}

function ConnectorPackageActivationRows({
  agentId,
  packages,
  loading,
  pending,
  onActivate,
}: {
  agentId?: string;
  packages: Array<{
    connectorPackageId: string;
    connectorKey: string;
    displayName: string;
    publishedVersion?: number | null;
    status: string;
  }>;
  loading: boolean;
  pending: boolean;
  onActivate: (connectorPackageId: string) => void;
}) {
  const publishedPackages = packages.filter((item) => item.publishedVersion);
  if (loading) {
    return <EmptyHint>Loading connector packages...</EmptyHint>;
  }
  if (publishedPackages.length === 0) {
    return <EmptyHint>No published connector packages are available.</EmptyHint>;
  }
  return (
    <div className="grid gap-2">
      {publishedPackages.map((item) => (
        <div
          key={item.connectorPackageId}
          className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-medium" style={{ color: "var(--text-strong)" }}>
              {item.displayName}
            </p>
            <p className="truncate text-xs font-mono" style={{ color: "var(--text-muted)" }}>
              {item.connectorKey} · v{item.publishedVersion}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!agentId || pending}
            onClick={() => onActivate(item.connectorPackageId)}
          >
            Activate
          </Button>
        </div>
      ))}
    </div>
  );
}

function PolicySection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-[var(--radius-md)] bg-[var(--bg-elevated)] px-4 py-5">
      <div className="mb-4 space-y-1">
        <h3 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
          {title}
        </h3>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          {description}
        </p>
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

function FieldGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </Label>
      {children}
    </div>
  );
}

function ConnectorSection({
  title,
  description,
  rows,
  onToggle,
  onSetDefault,
}: {
  title: string;
  description: string;
  rows: Array<{
    id: string;
    bindingId: string;
    targetKey?: string;
    title: string;
    description: string;
    supportingText: string;
    isSelected: boolean;
    isDefault: boolean;
    kind: "graphrag_source" | "connector_binding";
  }>;
  onToggle: (rowId: string, enabled: boolean) => void;
  onSetDefault: (bindingId: string, targetKey: string) => void;
}) {
  return (
    <section className="space-y-3">
      <div className="mb-3 space-y-1">
        <h4 className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
          {title}
        </h4>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          {description}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {rows.map((row) => (
          <div
            key={row.id}
            className="rounded-[var(--radius-md)] border px-3 py-3 transition-colors"
            style={{
              background: row.isSelected ? "var(--accent-subtle)" : "var(--bg-elevated)",
              borderColor: row.isSelected ? "var(--accent)" : "var(--border)",
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <Label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={row.isSelected}
                  onCheckedChange={(checked) => onToggle(row.id, checked === true)}
                  className="mt-0.5"
                />
                <span className="space-y-1">
                  <span className="block text-sm font-medium" style={{ color: "var(--text-strong)" }}>
                    {row.title}
                  </span>
                  <span className="block text-xs" style={{ color: "var(--text)" }}>
                    {row.description}
                  </span>
                  <span className="block text-[11px]" style={{ color: "var(--text-muted)" }}>
                    {row.supportingText}
                  </span>
                </span>
              </Label>

              {row.kind === "graphrag_source" && row.isSelected ? (
                row.isDefault ? (
                  <span className="shrink-0 rounded-[var(--radius-sm)] bg-[var(--bg-subtle)] px-2 py-1 text-xs" style={{ color: "var(--text-muted)" }}>
                    Default
                  </span>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => row.targetKey && onSetDefault(row.bindingId, row.targetKey)}
                  >
                    Set Default
                  </Button>
                )
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function BooleanRow({
  checked,
  label,
  description,
  onChange,
}: {
  checked: boolean;
  label: string;
  description: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="rounded-[var(--radius-md)] border border-border bg-[var(--bg-elevated)] px-3 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
            {label}
          </div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            {description}
          </div>
        </div>
        <Switch
          checked={checked}
          onCheckedChange={onChange}
          className="mt-0.5"
        />
      </div>
    </div>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div
      className="text-xs"
      style={{ color: "var(--text-muted)" }}
    >
      {children}
    </div>
  );
}

function JsonTextarea({
  value,
  onChange,
  rows = 5,
}: {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}) {
  return (
    <Textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      className="resize-y bg-[var(--bg-elevated)] font-mono text-xs"
      style={{ color: "var(--text)" }}
      spellCheck={false}
    />
  );
}
